import { PageSettings, Product } from '../types/pricingPage';
import { StripePrice } from '../types/stripePrice';
import { StripeTier } from '../types/stripeProduct';
import { isCustomProduct, isRecurringProduct, isStripePrice } from './products';

export const formatPrice = (data: Product, settings?: PageSettings, tier?: StripeTier, currency?: string) => {
    if (isStripePrice(data)) {
        if (!!currency && !!data.currency_options?.[currency.toLowerCase()]) {
            const unitAmount = data.currency_options[currency.toLowerCase()].unit_amount_decimal
                ? parseInt(data.currency_options[currency.toLowerCase()].unit_amount_decimal)
                : data.currency_options[currency.toLowerCase()].unit_amount;
            return formatPriceData(
                {
                    billing_scheme: data.billing_scheme,
                    currency,
                    interval: data.recurring?.interval,
                    unit_amount: unitAmount,
                },
                settings,
                tier
            );
        }

        return formatPriceData(
            {
                billing_scheme: data.billing_scheme,
                currency: data.currency,
                interval: data.recurring?.interval,
                unit_amount: data.unit_amount,
            },
            settings,
            tier
        );
    } else {
        return data.price.customLabel;
    }
};

export const formatPriceData = (
    data: { unit_amount: number; interval?: string; currency: string; billing_scheme: string },
    settings?: PageSettings,
    tier?: StripeTier
) => {
    const { currency } = data;
    const interval = settings?.displayMonthlyPrice && data.interval === 'year' ? 'year' : 'month';
    const format = (amount: number) => formatAmount({ amount, interval, currency });

    if (data.billing_scheme === 'per_unit') {
        return format(data.unit_amount || 0);
    } else if (tier) {
        const tieredPricing: string[] = [];
        if (tier.flat_amount) {
            tieredPricing.push(format(tier.flat_amount));
        }
        if (tier.unit_amount) {
            tieredPricing.push(format(tier.unit_amount || 0));
        }
        return tieredPricing.join(' + ');
    } else if (data.billing_scheme === 'tiered') {
        // used when previewing tiered products
        return 'From ' + format(data.unit_amount || 0);
    } else {
        return data.billing_scheme;
    }
};

export const formatFrequency = (product: Product, settings?: PageSettings) => {
    if (isCustomProduct(product) || !isRecurringProduct(product)) {
        return '';
    } else {
        return `/${getIntervalWording(product, settings)}`;
    }
};

const getIntervalWording = (product: StripePrice, settings?: PageSettings) => {
    const weekWording = settings?.customWording?.weekAbbreviation ?? 'week';
    const monthWording = settings?.customWording?.monthAbbreviation ?? 'mo';
    const monthsWording = settings?.customWording?.monthsAbbreviation ?? 'months';
    const yearWording = settings?.customWording?.yearAbbreviation ?? 'year';

    if (product.recurring?.interval === 'day') {
        return product.recurring?.interval_count > 1 ? product.recurring.interval_count + ' days' : 'day';
    }
    if (product.recurring?.interval === 'week') {
        return product.recurring?.interval_count > 1 ? product.recurring.interval_count + ' weeks' : weekWording;
    } else if (product.recurring?.interval === 'month' && product.recurring?.interval_count > 1) {
        return `${product.recurring.interval_count} ${monthsWording}`;
    } else if (settings?.displayMonthlyPrice || product.recurring?.interval === 'month') {
        return monthWording;
    } else if (product.recurring?.interval === 'year') {
        return yearWording;
    } else {
        return product.recurring?.interval;
    }
};

export const formatBillingPeriod = (product: Product, settings?: PageSettings) => {
    if (isCustomProduct(product) || !isRecurringProduct(product) || product.recurring?.interval === 'month') {
        return '';
    } else if (settings?.displayMonthlyPrice && product.recurring?.interval === 'year') {
        return `billed ${product.recurring?.interval}ly`;
    } else {
        return '';
    }
};

export const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(amount).replace('.00', '');
};

const ZERO_DECIMAL_CURRENCIES = [
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
];

export function formatAmount(data: {
    amount: number;
    interval: 'year' | 'month';
    currency: string;
    denomination?: 'cents' | 'dollars';
}) {
    const amountPerPeriod =
        data.amount /
        ((data.denomination || 'cents') === 'dollars' || ZERO_DECIMAL_CURRENCIES.includes(data.currency.toUpperCase())
            ? 1
            : 100);
    const amount = data.interval === 'year' ? amountPerPeriod / 12 : amountPerPeriod;
    return `${formatCurrency(amount, data.currency)}`;
}
